// EduBridge Network — Public website shared header + footer
// Matches the live brand: orange top bar, white nav, world-map orange footer.
import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, MapPin, Phone, Mail, Bell, Facebook, Instagram, Linkedin, Youtube, Twitter, Menu, X, ChevronDown, GraduationCap, Briefcase, User, FileText, LogOut } from 'lucide-react';
import PoweredBy from '../PoweredBy';
import { useAuth } from '../../App';
import { useSiteSettings } from '../../hooks/useSiteSettings';

const NAV_LINKS = [
  { to: '/courses', label: 'Find by Programs' },
  { to: '/countries', label: 'Find by Countries' },
];

export const PublicHeader = () => {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const site = useSiteSettings();

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/courses?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header className="w-full" data-testid="public-header">
      {/* Top orange contact bar */}
      <div className="bg-[#F5A623] text-[#1A2A4F]">
        <div className="max-w-7xl mx-auto px-4 py-2.5 flex flex-wrap items-center justify-between gap-3 text-xs sm:text-sm font-medium">
          <div className="flex flex-wrap items-center gap-5">
            {site.primary_address && (
              <span className="flex items-center gap-1.5" data-testid="header-address"><MapPin className="w-3.5 h-3.5" /> {site.primary_address}</span>
            )}
            {(site.phones || []).slice(0, 1).map((p, i) => (
              <a key={i} href={`tel:${p.replace(/\s+/g, '')}`} className="flex items-center gap-1.5 hover:opacity-80" data-testid="header-phone"><Phone className="w-3.5 h-3.5" /> {p}</a>
            ))}
            {(site.emails || []).slice(0, 1).map((em, i) => (
              <a key={i} href={`mailto:${em}`} className="flex items-center gap-1.5 hover:opacity-80" data-testid="header-email"><Mail className="w-3.5 h-3.5" /> {em}</a>
            ))}
          </div>
          <div className="flex items-center gap-3">
            {site.social?.facebook && (<a href={site.social.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" className="hover:opacity-70" data-testid="social-facebook"><Facebook className="w-4 h-4" /></a>)}
            {site.social?.instagram && (<a href={site.social.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="hover:opacity-70" data-testid="social-instagram"><Instagram className="w-4 h-4" /></a>)}
            {site.social?.linkedin && (<a href={site.social.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn" className="hover:opacity-70" data-testid="social-linkedin"><Linkedin className="w-4 h-4" /></a>)}
            {site.social?.youtube && (<a href={site.social.youtube} target="_blank" rel="noreferrer" aria-label="YouTube" className="hover:opacity-70" data-testid="social-youtube"><Youtube className="w-4 h-4" /></a>)}
            {site.social?.twitter && (<a href={site.social.twitter} target="_blank" rel="noreferrer" aria-label="Twitter/X" className="hover:opacity-70" data-testid="social-twitter"><Twitter className="w-4 h-4" /></a>)}
          </div>
        </div>
      </div>

      {/* White nav with logo + search + links */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/" className="flex items-center shrink-0" data-testid="public-logo-link">
            {site.logo_url ? (
              <img
                src={site.logo_url.startsWith('http') ? site.logo_url : `${process.env.REACT_APP_BACKEND_URL}${site.logo_url}`}
                alt={site.company_name || 'Edu Bridge Network'}
                className="h-9 w-auto max-w-[220px] object-contain"
                data-testid="public-logo-img"
              />
            ) : (
              <span className="text-[#1A2A4F] font-bold text-xl tracking-tight">{site.company_name || 'Edu Bridge Network'}</span>
            )}
          </Link>

          {/* Search bar (centerpiece, like screenshots) */}
          <form onSubmit={onSearch} className="hidden md:flex flex-1 max-w-2xl mx-auto" data-testid="public-search-form">
            <div className="flex items-center w-full rounded-full border border-gray-200 px-4 py-2.5 focus-within:border-[#F5A623] focus-within:ring-2 focus-within:ring-[#F5A623]/20 transition">
              <Search className="w-4 h-4 text-gray-400 shrink-0" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search universities, courses, countries..."
                className="ml-2 flex-1 outline-none text-sm bg-transparent text-[#1A2A4F]"
                data-testid="public-search-input"
              />
            </div>
          </form>

          {/* Desktop links */}
          <nav className="hidden lg:flex items-center gap-5 text-sm font-medium text-[#1A2A4F]">
            {NAV_LINKS.map((l) => (
              <Link key={l.to} to={l.to} className="hover:text-[#F5A623] transition-colors" data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, '-')}`}>{l.label}</Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2">
            {user ? <StudentHeaderActions /> : (
              <>
                <RegisterDropdown />
                <Link
                  to="/login"
                  className="bg-[#1A2A4F] text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:opacity-90 transition shadow"
                  data-testid="header-signin-btn"
                >
                  Sign In
                </Link>
              </>
            )}
          </div>

          <button className="lg:hidden ml-auto p-2" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="header-menu-btn">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile nav drawer */}
        {open && (
          <div className="lg:hidden border-t border-gray-100 bg-white" data-testid="mobile-nav">
            <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col gap-2 text-[#1A2A4F]">
              {NAV_LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className="py-2 text-sm font-medium hover:text-[#F5A623]"
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {l.label}
                </Link>
              ))}
              {user ? (
                <>
                  <Link to="/my-account" onClick={() => setOpen(false)} className="mt-1 bg-[#1A2A4F] text-white font-semibold px-4 py-2 rounded-full text-center text-sm" data-testid="mobile-my-account-btn">My Account</Link>
                  <Link to="/my-account/applications" onClick={() => setOpen(false)} className="bg-[#F5A623] text-white font-semibold px-4 py-2 rounded-full text-center text-sm" data-testid="mobile-my-applications-btn">My Applications</Link>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="mt-1 bg-[#1A2A4F] text-white font-semibold px-4 py-2 rounded-full text-center text-sm" data-testid="mobile-signin-btn">Sign In</Link>
                  <Link to="/register?as=student" onClick={() => setOpen(false)} className="bg-[#F5A623] text-white font-semibold px-4 py-2 rounded-full text-center text-sm" data-testid="mobile-register-student-btn">Register as Student</Link>
                  <Link to="/register?as=agent" onClick={() => setOpen(false)} className="border border-[#F5A623] text-[#F5A623] font-semibold px-4 py-2 rounded-full text-center text-sm" data-testid="mobile-register-agent-btn">Register as Agent</Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

// Logged-in student: avatar pill + notification bell. Tightly matches the
// reference screenshot (orange pill avatar with name + blue bell).
const StudentHeaderActions = () => {
  const { user, logout, api } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [recent, setRecent] = useState([]);
  const menuRef = useRef(null);
  const bellRef = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (bellRef.current && !bellRef.current.contains(e.target)) setBellOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  // Poll unread count every 60s
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const r = await api().get('/student/me/unread-count');
        if (alive) setUnread(r.data?.count || 0);
      } catch { /* silent */ }
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [api]);

  const openBell = async () => {
    setBellOpen((o) => !o);
    if (!bellOpen) {
      try {
        const r = await api().get('/student/me/notifications');
        setRecent(Array.isArray(r.data) ? r.data.slice(0, 6) : []);
      } catch { setRecent([]); }
    }
  };

  const initials = (user?.name || user?.email || 'S')
    .split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className="flex items-center gap-3" data-testid="student-header-actions">
      {/* Bell */}
      <div className="relative" ref={bellRef}>
        <button
          type="button"
          onClick={openBell}
          aria-label="Notifications"
          className="relative w-10 h-10 rounded-full border border-[#1A2A4F]/15 text-[#1A2A4F] hover:bg-[#1A2A4F]/5 flex items-center justify-center transition"
          data-testid="student-bell-btn"
        >
          <Bell className="w-4 h-4" />
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-[#F5A623] text-white text-[10px] font-bold flex items-center justify-center px-1" data-testid="student-bell-unread">
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
        {bellOpen && (
          <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50" data-testid="student-bell-dropdown">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <p className="font-bold text-[#1A2A4F] text-sm">Notifications</p>
              <Link to="/my-account/notifications" onClick={() => setBellOpen(false)}
                className="text-xs font-semibold text-[#F5A623] hover:underline">View all</Link>
            </div>
            {recent.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-gray-500" data-testid="student-bell-empty">
                You're all caught up. 🎉
              </p>
            ) : (
              <ul className="max-h-80 overflow-auto divide-y divide-gray-50">
                {recent.map((n) => (
                  <li key={n.id} className={`px-4 py-3 hover:bg-gray-50 ${n.read ? '' : 'bg-[#F5A623]/5'}`}>
                    <p className="text-sm font-semibold text-[#1A2A4F] line-clamp-1">{n.title || n.subject || 'Update'}</p>
                    {n.body && <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{n.body}</p>}
                    <p className="text-[10px] text-gray-400 mt-1">{n.created_at ? new Date(n.created_at).toLocaleString() : ''}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Profile pill */}
      <div className="relative" ref={menuRef}>
        <button
          type="button"
          onClick={() => setMenuOpen((o) => !o)}
          className="flex items-center gap-2.5 pl-1.5 pr-4 py-1.5 rounded-full bg-[#F5A623] text-white shadow hover:brightness-95 transition"
          data-testid="student-profile-pill"
          aria-expanded={menuOpen}
        >
          <span className="w-8 h-8 rounded-full bg-white text-[#F5A623] text-xs font-extrabold flex items-center justify-center shadow-inner shrink-0" data-testid="student-avatar">
            {initials}
          </span>
          <span className="text-sm font-semibold max-w-[140px] truncate ml-0.5" data-testid="student-name-label">
            {user?.name || user?.email?.split('@')[0] || 'Student'}
          </span>
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${menuOpen ? 'rotate-180' : ''}`} />
        </button>
        {menuOpen && (
          <div className="absolute right-0 mt-2 w-60 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50" data-testid="student-profile-menu">
            <div className="px-4 py-3 bg-[#1A2A4F] text-white">
              <p className="text-sm font-bold leading-tight">{user?.name || 'Student'}</p>
              <p className="text-[11px] opacity-70 truncate">{user?.email}</p>
            </div>
            <button onClick={() => { setMenuOpen(false); navigate('/my-account'); }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#1A2A4F] hover:bg-[#F5A623]/10"
              data-testid="menu-my-account">
              <User className="w-4 h-4 text-[#F5A623]" /> My Account
            </button>
            <button onClick={() => { setMenuOpen(false); navigate('/my-account/applications'); }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#1A2A4F] hover:bg-[#F5A623]/10"
              data-testid="menu-my-applications">
              <FileText className="w-4 h-4 text-[#F5A623]" /> My Applications
            </button>
            <button onClick={() => { setMenuOpen(false); navigate('/my-account/notifications'); }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#1A2A4F] hover:bg-[#F5A623]/10"
              data-testid="menu-my-notifications">
              <Bell className="w-4 h-4 text-[#F5A623]" />
              Notifications {unread > 0 && <span className="ml-auto bg-[#F5A623] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{unread}</span>}
            </button>
            <button onClick={async () => { setMenuOpen(false); await logout(); navigate('/'); }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-rose-600 border-t border-gray-100 hover:bg-rose-50"
              data-testid="menu-logout">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const RegisterDropdown = () => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="bg-[#1A2A4F] text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:opacity-90 transition shadow flex items-center gap-1.5"
        data-testid="header-register-btn"
        aria-expanded={open}
      >
        Register <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50" data-testid="register-dropdown">
          <Link
            to="/register?as=student"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-4 py-3 hover:bg-[#F5A623]/10 transition border-b border-gray-50"
            data-testid="register-dropdown-student"
          >
            <div className="w-9 h-9 rounded-full bg-[#F5A623]/15 text-[#F5A623] flex items-center justify-center">
              <GraduationCap className="w-4 h-4" />
            </div>
            <div>
              <p className="font-semibold text-sm text-[#1A2A4F]">Student</p>
              <p className="text-xs text-gray-500">Apply to programs</p>
            </div>
          </Link>
          <Link
            to="/register?as=agent"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-4 py-3 hover:bg-[#F5A623]/10 transition"
            data-testid="register-dropdown-agent"
          >
            <div className="w-9 h-9 rounded-full bg-[#1A2A4F]/10 text-[#1A2A4F] flex items-center justify-center">
              <Briefcase className="w-4 h-4" />
            </div>
            <div>
              <p className="font-semibold text-sm text-[#1A2A4F]">Agent</p>
              <p className="text-xs text-gray-500">Submit applications</p>
            </div>
          </Link>
        </div>
      )}
    </div>
  );
};

export const PublicFooter = () => {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const site = useSiteSettings();

  const onSubscribe = (e) => {
    e.preventDefault();
    if (email && /@/.test(email)) {
      setSent(true);
      setEmail('');
      setTimeout(() => setSent(false), 3000);
    }
  };

  return (
    <footer className="relative bg-[#F5A623] text-[#1A2A4F]" data-testid="public-footer">
      {/* Subtle world-map watermark overlay */}
      <div
        className="absolute inset-0 opacity-10 pointer-events-none"
        style={{
          backgroundImage:
            'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 1200 400\'><text x=\'50%25\' y=\'50%25\' text-anchor=\'middle\' fill=\'%231A2A4F\' font-size=\'320\' font-family=\'serif\' font-weight=\'bold\' opacity=\'0.4\'>🌍</text></svg>")',
          backgroundSize: 'cover',
        }}
      />
      <div className="relative max-w-7xl mx-auto px-4 py-12 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div>
          <h3 className="font-bold text-lg mb-4">{site.company_name || 'Edu Bridge Network'}</h3>
          {(site.secondary_address || site.primary_address) && (
            <p className="flex items-start gap-2 text-sm leading-relaxed mb-3" data-testid="footer-address">
              <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
              <span style={{ whiteSpace: 'pre-line' }}>{site.secondary_address || site.primary_address}</span>
            </p>
          )}
          {(site.emails || []).map((em, i) => (
            <p key={i} className="flex items-center gap-2 text-sm" data-testid={`footer-email-${i}`}>
              <Mail className="w-4 h-4" /> <a href={`mailto:${em}`} className="hover:underline">{em}</a>
            </p>
          ))}
          {(site.phones || []).map((p, i) => (
            <p key={i} className="flex items-center gap-2 text-sm mt-1" data-testid={`footer-phone-${i}`}>
              <Phone className="w-4 h-4" /> <a href={`tel:${p.replace(/\s+/g, '')}`} className="hover:underline">{p}</a>
            </p>
          ))}
        </div>

        <div>
          <h3 className="font-bold text-lg mb-4">Useful Link</h3>
          <ul className="space-y-2 text-sm">
            <li><Link to="/courses" className="hover:underline">› Subject</Link></li>
            <li><Link to="/countries" className="hover:underline">› Countries</Link></li>
            <li><Link to="/universities" className="hover:underline">› Universities</Link></li>
            <li><Link to="/scholarships" className="hover:underline">› Scholarships</Link></li>
          </ul>
        </div>

        <div>
          <h3 className="font-bold text-lg mb-4">Support</h3>
          <ul className="space-y-2 text-sm">
            <li><Link to="/about" className="hover:underline">› About Us</Link></li>
            <li><Link to="/contact" className="hover:underline">› Contact</Link></li>
            <li><Link to="/faq" className="hover:underline">› FAQ Page</Link></li>
            <li><Link to="/privacy" className="hover:underline">› Privacy Policy</Link></li>
            <li><Link to="/terms" className="hover:underline">› Terms And Condition</Link></li>
          </ul>
        </div>

        <div>
          <h3 className="font-bold text-lg mb-4">Newsletter</h3>
          <p className="text-sm mb-3 leading-relaxed">Subscribe to the free newsletter and stay up to date</p>
          <form onSubmit={onSubscribe} className="bg-white rounded-full p-1 flex items-center" data-testid="newsletter-form">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email address"
              className="flex-1 px-4 py-2 text-sm outline-none rounded-full text-[#1A2A4F] bg-transparent"
              data-testid="newsletter-input"
            />
            <button
              type="submit"
              className="bg-[#1A2A4F] text-white font-semibold px-5 py-2 rounded-full text-sm hover:opacity-90"
              data-testid="newsletter-submit-btn"
            >
              Send
            </button>
          </form>
          {sent && <p className="text-sm mt-2 font-medium" data-testid="newsletter-success">Thanks — you're subscribed!</p>}
        </div>
      </div>

      <div className="relative border-t border-[#1A2A4F]/20">
        <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs">
          <PoweredBy />
          <div className="flex items-center gap-4">
            <Link to="/about" className="hover:underline">About Us</Link>
            <Link to="/privacy" className="hover:underline">Privacy Policy</Link>
            <Link to="/terms" className="hover:underline">Terms &amp; Conditions</Link>
            <Link to="/contact" className="hover:underline">Contact</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

const PublicLayout = ({ children }) => (
  <div className="min-h-screen flex flex-col bg-white text-[#1A2A4F]">
    <PublicHeader />
    <main className="flex-1">{children}</main>
    <PublicFooter />
  </div>
);

export default PublicLayout;
